import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/**
 * LinkedIn OAuth callback handler.
 *
 * Flow:
 *   1. User clicks "Connect LinkedIn" on /social/accounts
 *   2. useConnectLinkedIn() POSTs to relay-social-linkedin-authorize → returns
 *      authorization_url. Browser is redirected to LinkedIn Login.
 *   3. After consent, LinkedIn redirects here with ?code=XXX&state=YYY
 *   4. This page POSTs { code, state, redirect_uri } to
 *      relay-social-linkedin-callback, which exchanges the code for an
 *      access token, fetches the member profile via OIDC userinfo, and
 *      upserts a row into relay.social_accounts.
 *   5. On success, navigates to /social/accounts.
 *
 * IMPORTANT: redirect_uri must be byte-identical to the value sent to
 * relay-social-linkedin-authorize. Both use:
 *   window.location.origin + "/auth/callback/linkedin"
 */

type Stage = "exchanging" | "success" | "error";

interface LinkedInCallbackResponse {
  connected: number;
  accounts: Array<{
    platform: string;
    platform_account_id: string;
    account_name: string;
  }>;
  error?: string;
}

export default function AuthCallbackLinkedIn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>("exchanging");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const code        = params.get("code");
  const state       = params.get("state");
  const oauthError  = params.get("error");
  const oauthErrorDescription = params.get("error_description");

  useEffect(() => {
    let cancelled = false;

    async function exchange() {
      // LinkedIn redirects with ?error= when the user cancels or denies.
      if (oauthError) {
        if (cancelled) return;
        setStage("error");
        setErrorMsg(oauthErrorDescription ?? oauthError);
        return;
      }

      if (!code || !state) {
        if (cancelled) return;
        setStage("error");
        setErrorMsg("Missing authorization code or state in callback URL.");
        return;
      }

      const redirectUri = `${window.location.origin}/auth/callback/linkedin`;
      const { data, error } = await supabase.functions.invoke<LinkedInCallbackResponse>(
        "relay-social-linkedin-callback",
        { body: { code, state, redirect_uri: redirectUri } },
      );

      if (cancelled) return;

      if (error) {
        setStage("error");
        setErrorMsg(error.message ?? "Token exchange failed.");
        return;
      }

      if (data?.error) {
        setStage("error");
        setErrorMsg(data.error);
        return;
      }

      setStage("success");
      setTimeout(() => {
        if (!cancelled) navigate("/social/accounts", { replace: true });
      }, 1500);
    }

    void exchange();
    return () => { cancelled = true; };
  }, [code, state, oauthError, oauthErrorDescription, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-6 py-12">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            {stage === "exchanging" && (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> Connecting your LinkedIn account…
              </>
            )}
            {stage === "success" && (
              <>
                <CheckCircle2 className="h-5 w-5 text-emerald-600" /> Connected
              </>
            )}
            {stage === "error" && (
              <>
                <AlertTriangle className="h-5 w-5 text-destructive" /> Connection failed
              </>
            )}
          </CardTitle>
          <CardDescription>
            {stage === "exchanging" && "Hold on — exchanging the authorization code for tokens."}
            {stage === "success" && "Your LinkedIn personal profile is now connected to Relay. Redirecting…"}
            {stage === "error" && (errorMsg ?? "Something went wrong.")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            type="button"
            className="w-full"
            onClick={() => navigate("/social/accounts", { replace: true })}
          >
            Back to social accounts
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
